// ---------------------------------------------------------------------------
// PWA Caisse Tablette — API Client
// HTTP client for communicating with the Flask backend via same-origin proxy.
// Singleton with retry, timeout, cookie auth, and CSRF handling.
// ---------------------------------------------------------------------------

import { useConfigStore } from '@/store/config-store';
import { useAuthStore } from '@/store/auth-store';
import { API_TIMEOUT, RETRY_DELAYS } from '@/lib/constants';
import { processProducts } from '@/lib/price-formatter';
import type {
  Product,
  Transaction,
  HeartbeatResponse,
  CashSession,
  FloorPlan,
  Table,
  Reservation,
  PosConfig,
} from '@/lib/types';

// ---------------------------------------------------------------------------
// CSRF helper
// ---------------------------------------------------------------------------

function getCsrfToken(): string {
  const match = document.cookie.match(/csrf_access_token=([^;]+)/);
  return match ? match[1] : '';
}

// ---------------------------------------------------------------------------
// Response types (internal)
// ---------------------------------------------------------------------------

interface ProductsResponse {
  success?: boolean;
  products: Product[];
}

interface ReservationsResponse {
  success?: boolean;
  reservations: Reservation[];
}

interface FloorPlansResponse {
  success?: boolean;
  floor_plans: FloorPlan[];
}

interface TablesResponse {
  success?: boolean;
  tables: Record<string, Table>;
}

interface TransactionResponse {
  success?: boolean;
  transaction_id?: string;
  id?: number;
  [key: string]: unknown;
}

interface BatchSyncResponse {
  success?: boolean;
  synced?: number;
  errors?: number;
}

interface TicketsResponse {
  success?: boolean;
  transactions?: TransactionResponse[];
  [key: string]: unknown;
}

interface CashSessionResponse {
  success?: boolean;
  session: CashSession | null;
}

interface ReceiptResponse {
  success?: boolean;
  [key: string]: unknown;
}

interface HealthResponse {
  status: string;
  version?: string;
}

interface SyncDataResult {
  success: boolean;
  data: {
    products: ProductsResponse | null;
    reservations: ReservationsResponse | null;
    tables: TablesResponse | null;
    config: PosConfig | null;
  };
  error?: string;
}

// ---------------------------------------------------------------------------
// API Client class
// ---------------------------------------------------------------------------

class ApiClient {
  private readonly maxRetries = 3;

  // ---- private helpers ----------------------------------------------------

  private getUrl(endpoint: string): string {
    return `/api${endpoint}`;
  }

  private getHeaders(mutating = false): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (mutating) {
      headers['X-CSRF-TOKEN'] = getCsrfToken();
    }
    return headers;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // ---- core request with retry + timeout + 401 handling -------------------

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = this.getUrl(endpoint);
    const method = (options.method ?? 'GET').toUpperCase();
    const isMutating = method !== 'GET' && method !== 'HEAD';
    const headers = {
      ...this.getHeaders(isMutating),
      ...(options.headers as Record<string, string> | undefined),
    };

    let lastError: Error | null = null;

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

      try {
        const response = await fetch(url, {
          ...options,
          headers,
          credentials: 'include',
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        // 401 -> session expired, force logout
        if (response.status === 401) {
          console.warn('[API] 401 Unauthorized -- session expired');
          useAuthStore.getState().logout();
          throw new Error('Session expiree, reconnectez-vous');
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            (errorData as { message?: string }).message ||
              `HTTP ${response.status}: ${response.statusText}`,
          );
        }

        return (await response.json()) as T;
      } catch (error) {
        clearTimeout(timeoutId);
        lastError = error instanceof Error ? error : new Error(String(error));

        // Do not retry on 401 or abort
        if (
          lastError.message.includes('Session expiree') ||
          lastError.name === 'AbortError'
        ) {
          throw lastError;
        }

        console.warn(
          `[API] Attempt ${attempt + 1}/${this.maxRetries} failed:`,
          lastError.message,
        );

        if (attempt < this.maxRetries - 1) {
          const backoff = RETRY_DELAYS[attempt] ?? 1000;
          await this.delay(backoff);
        }
      }
    }

    throw lastError ?? new Error('Request failed after retries');
  }

  // ========== STATUS / HEALTH ==============================================

  async getStatus(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  async heartbeat(): Promise<HeartbeatResponse> {
    const { posId, deviceId } = useConfigStore.getState();
    if (!posId) return { status: 'ok' } as HeartbeatResponse;

    return this.request<HeartbeatResponse>(`/pos/${posId}/heartbeat`, {
      method: 'POST',
      body: JSON.stringify({
        device_id: deviceId,
        timestamp: new Date().toISOString(),
      }),
    });
  }

  async testConnection(): Promise<{ connected: boolean; status?: HealthResponse; error?: string }> {
    try {
      const status = await this.getStatus();
      return { connected: true, status };
    } catch (error) {
      return { connected: false, error: (error as Error).message };
    }
  }

  // ========== PRODUCTS =====================================================

  async getProducts(): Promise<ProductsResponse> {
    const data = await this.request<ProductsResponse>('/pos/products');

    if (data.products) {
      data.products = processProducts(data.products);
    }

    return data;
  }

  // ========== RESERVATIONS =================================================

  async getReservations(): Promise<ReservationsResponse> {
    return this.request<ReservationsResponse>('/reservations');
  }

  async searchReservation(query: string): Promise<ReservationsResponse> {
    return this.request<ReservationsResponse>(
      `/reservations?search=${encodeURIComponent(query)}`,
    );
  }

  async createInstantReservation(
    tableId: string,
    clientInfo: { name?: string; phone?: string; nb_persons?: number },
  ): Promise<TransactionResponse> {
    return this.request<TransactionResponse>('/reservations', {
      method: 'POST',
      body: JSON.stringify({
        table_id: tableId,
        client_info: clientInfo,
        type: 'walk_in',
      }),
    });
  }

  async transferReservation(
    reservationId: string,
    newTableId: string,
  ): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(
      `/reservations/${reservationId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ table_id: newTableId }),
      },
    );
  }

  async deleteReservation(reservationId: string): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(
      `/reservations/${reservationId}`,
      { method: 'DELETE' },
    );
  }

  // ========== TABLES / FLOOR PLANS =========================================

  async getFloorPlans(): Promise<FloorPlansResponse> {
    return this.request<FloorPlansResponse>('/restaurant/floor-plan/timeline');
  }

  async getTablesStatus(): Promise<TablesResponse> {
    return this.request<TablesResponse>('/restaurant/tables');
  }

  async lockTable(tableId: string, duration = 300): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(`/restaurant/tables/${tableId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'locked', lock_duration: duration }),
    });
  }

  async releaseTable(tableId: string): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(`/restaurant/tables/${tableId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'available' }),
    });
  }

  // ========== TRANSACTIONS =================================================

  async createTransaction(data: Record<string, unknown>): Promise<TransactionResponse> {
    const { deviceId } = useConfigStore.getState();

    return this.request<TransactionResponse>('/transactions', {
      method: 'POST',
      body: JSON.stringify({
        ...data,
        source: 'caisse',
        agent_id: deviceId,
      }),
    });
  }

  async batchSync(
    transactions: Record<string, unknown>[],
  ): Promise<BatchSyncResponse> {
    const { deviceId } = useConfigStore.getState();

    return this.request<BatchSyncResponse>('/transactions/batch', {
      method: 'POST',
      body: JSON.stringify({
        transactions,
        source: 'caisse',
        device_id: deviceId,
      }),
    });
  }

  async getTodayTickets(): Promise<TicketsResponse> {
    const { posId } = useConfigStore.getState();
    return this.request<TicketsResponse>(
      `/transactions?source=caisse&pos_id=${posId}&period=today`,
    );
  }

  async getTicketsHistory(): Promise<TicketsResponse> {
    const { posId } = useConfigStore.getState();
    return this.request<TicketsResponse>(
      `/transactions?source=caisse&pos_id=${posId}`,
    );
  }

  async getTicket(ticketId: string | number): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(`/transactions/${ticketId}`);
  }

  // ========== FISCAL RECEIPTS (NF525) ======================================

  async getReceipt(transactionId: string | number): Promise<ReceiptResponse> {
    return this.request<ReceiptResponse>(`/fiscal/receipt/${transactionId}`);
  }

  async getDuplicateReceipt(
    transactionId: string | number,
  ): Promise<ReceiptResponse> {
    return this.request<ReceiptResponse>(
      `/fiscal/receipt/${transactionId}/duplicate`,
      { method: 'POST' },
    );
  }

  // ========== REFUND (NF525) ===============================================

  async refundTransaction(
    transactionId: string | number,
    reason: string,
  ): Promise<TransactionResponse> {
    return this.request<TransactionResponse>(
      `/transactions/${transactionId}/refund`,
      {
        method: 'POST',
        body: JSON.stringify({ reason }),
      },
    );
  }

  // ========== CASH CONTROL =================================================

  async getCurrentCashSession(): Promise<CashSessionResponse> {
    const { posId } = useConfigStore.getState();
    return this.request<CashSessionResponse>(
      `/cash-control/current?pos_id=${posId}`,
    );
  }

  async openCashSession(
    openingCashAmount = 0,
    posId?: number,
  ): Promise<CashSessionResponse> {
    const resolvedPosId = posId ?? useConfigStore.getState().posId;
    return this.request<CashSessionResponse>('/cash-control/open', {
      method: 'POST',
      body: JSON.stringify({
        opening_cash_amount: openingCashAmount,
        pos_id: resolvedPosId,
      }),
    });
  }

  async closeCashSession(
    actualCashAmount: number,
    notes?: string,
  ): Promise<CashSessionResponse> {
    return this.request<CashSessionResponse>('/cash-control/close', {
      method: 'POST',
      body: JSON.stringify({
        actual_cash_amount: actualCashAmount,
        notes: notes ?? null,
      }),
    });
  }

  // ========== CONFIG =======================================================

  async getCaisseConfig(): Promise<PosConfig> {
    const { posId } = useConfigStore.getState();
    return this.request<PosConfig>(`/pos/${posId}`);
  }

  // ========== POS LIST (for setup) =========================================

  async getPosList(): Promise<{ id: number; name: string; company_id: number; zone?: string; status?: string }[]> {
    const data = await this.request<{ pos_list?: unknown[]; data?: unknown[] }>('/pos?status=active');
    return (data.pos_list ?? data.data ?? []) as { id: number; name: string; company_id: number; zone?: string; status?: string }[];
  }

  // ========== SYNC (parallel) ==============================================

  async syncData(): Promise<SyncDataResult> {
    try {
      const [products, reservations, tables, config] = await Promise.allSettled([
        this.getProducts(),
        this.getReservations(),
        this.getTablesStatus(),
        this.getCaisseConfig(),
      ]);

      return {
        success: true,
        data: {
          products: products.status === 'fulfilled' ? products.value : null,
          reservations:
            reservations.status === 'fulfilled' ? reservations.value : null,
          tables: tables.status === 'fulfilled' ? tables.value : null,
          config: config.status === 'fulfilled' ? config.value : null,
        },
      };
    } catch (error) {
      console.error('[API] Sync error:', error);
      return {
        success: false,
        data: { products: null, reservations: null, tables: null, config: null },
        error: (error as Error).message,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const apiClient = new ApiClient();
